/**
 * User context menu dropdown.
 * Shows the currently logged-in user's name and a logout option.
 *
 * @module components/UserContextMenu
 */

import React, { useState, useRef, useEffect }  from 'react';
import { useNavigate }                         from 'react-router-dom';
import { UserCircle }                          from 'lucide-react';
import { useAuth }                             from '../dataProviders/AuthProvider';
import { useI18n }                             from '../dataProviders/I18nProvider';
import config                                  from '../config';

/**
 * Renders a user icon that toggles a dropdown menu on click.
 *
 * @returns {React.ReactElement}
 */
export default function UserContextMenu() {
  const [open, setOpen]  = useState(false);
  const ref              = useRef(null);
  const navigate         = useNavigate();
  const { user, logout } = useAuth();
  const { t }            = useI18n();

  /**
   * Close the dropdown when clicking outside.
   */
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Handle logout: clear session, navigate to home.
   */
  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate(config.pagePaths.home);
  };

  return (
    <div className="user-menu-container" ref={ref}>
      <button
        className="header-nav-link"
        onClick={() => setOpen(!open)}
        aria-label="User menu"
      >
        <UserCircle size={20} />
      </button>

      {open && (
        <div className="user-menu-dropdown">
          <div className="user-menu-label">
            {t('user.loggedInAs', { username: user?.username || '' })}
          </div>
          <div className="user-menu-divider" />
          <button className="user-menu-item" onClick={handleLogout}>
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
